import * as React from 'react'
import styled from 'styled-components'
import Image from 'gatsby-image/withIEPolyfill'
import { FluidObject } from 'gatsby-image'
import { Ingredient } from '../../data/Ingredient'
import { constants, FontSettings, TextSettingsProps } from '../../constants'
import TextBlock from '../common/TextBlock'
import { renderWithNewlines } from '../../render-utils'
import { FontsContext } from '../../context'

interface Props {
  ingredient: Ingredient
  fluidImage: FluidObject
  whatIs: string
}

const WhatAreThey: React.FC<Props> = ({ ingredient, fluidImage, whatIs }) => {
  const fonts = React.useContext(FontsContext)

  return (
    <Section>
      <ImageBlock>
        <Image fluid={fluidImage} alt={ingredient.name} />
      </ImageBlock>
      <DescriptionBlock>
        <WhatIs settings={fonts.texts.detailsWhatIs}>{whatIs}</WhatIs>
        <TitleBlock>
          <H1 europa={fonts.europa} settings={fonts.texts.detailsTitle}>
            {ingredient.name}
          </H1>
          {ingredient.subtitle && (
            <SubTitle settings={fonts.texts.detailsSubtitle}>
              {ingredient.subtitle}
            </SubTitle>
          )}
        </TitleBlock>
        <TextBlock>{renderWithNewlines(ingredient.whatAreThey)}</TextBlock>
      </DescriptionBlock>
    </Section>
  )
}

const Section = styled.div`
  margin-bottom: ${constants.spacing.sp.extraLarge};

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    display: flex;
  }
`

const TitleBlock = styled.div`
  margin-bottom: ${constants.spacing.sp.medium};

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    margin-bottom: 64px;
  }
`

const ImageBlock = styled.div`
  margin: 0 ${constants.spacing.sp.medium} ${constants.spacing.sp.medium};

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    flex: 0 0 460px;
    margin: 0;
    order: 2;
  }
`

const DescriptionBlock = styled.div`
  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    flex: 1;
    padding-right: 48px;
    order: 1;
  }
`

const WhatIs = styled.span<TextSettingsProps>`
  display: none;

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    display: block;
    font-size: ${({ settings }) => settings.pc.size};
    color: #999999;
    ${({ settings }) =>
      settings.pc.marginBottom && `margin-bottom: ${settings.pc.marginBottom};`}
  }
`

interface H1Props extends TextSettingsProps {
  europa: boolean
}

const H1 = styled.h1<H1Props>`
  margin: 0;
  font-size: ${({ settings }) => settings.sp.size};
  text-align: center;
  font-weight: normal;
  color: ${constants.colors.text.darker};
  ${({ settings }) =>
    settings.sp.weight && `font-weight: ${settings.sp.weight};`}

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    font-size: ${({ settings }) => settings.pc.size};
    text-align: left;
  }

  ${({ europa }) =>
    europa &&
    `
    @media only screen and (max-width: 959px) {
      font-family: europa;
    }
  `}
`

const SubTitle = styled.div<TextSettingsProps>`
  margin-top: ${constants.spacing.sp.small};
  font-size: ${({ settings }) => settings.sp.size};
  text-align: center;
  color: ${constants.colors.text.darker};

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    font-size: ${({ settings }) => settings.pc.size};
    text-align: left;
    ${({ settings }) =>
      settings.pc.lineHeight && `line-height: ${settings.pc.lineHeight}`};
    ${({ settings }) =>
      settings.pc.marginTop && `margin-top: ${settings.pc.marginTop}`};
  }
`

export default WhatAreThey
